import { FC, useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import MessagesList from '../MessagesList/MessagesList';
import { findDocuments } from '@/lib/functions/apiCalls';
import { FindDocuments, UploadDocumentResponse } from '@/lib/types/apiTypes';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store/store';
import useDebounce from '@/lib/hooks/useDebounce';
import { useParams } from 'react-router';
import { findSection } from '@/lib/functions/funcUtils';

type DocumentType = 'section' | 'formative';

interface DocumentChatBoxProps {
	documentType?: DocumentType;
	setDocLoading: (loading: boolean) => void;
	setDocData: (docData: UploadDocumentResponse | null) => void;
}

const DocumentChatBox: FC<DocumentChatBoxProps> = ({
	documentType,
	setDocLoading,
	setDocData,
}) => {
	const proposal = useSelector((state: RootState) => state.proposal.proposal);
	const [queryMessage, setQueryMessage] = useState('');
	const [documents, setDocuments] = useState<FindDocuments[]>();
	const [isMessageLoading, setIsMessageLoading] = useState(false);
	const [isSuggestedList, setIsSuggestedList] = useState(true);

	const { nodeId } = useParams();

	const getSectionDocuments = () => {
		const section = findSection(proposal?.sections || [], nodeId as string);
		return section?.sectionDocuments.map((section) => section.document);
	};

	const getFilteredDocuments = (documentsResponse) => {
		return attachedDocuments
			? documentsResponse.filter(
					(document) =>
						!attachedDocuments?.find(
							(attachedDocument) => attachedDocument?.id === document.documentId
						)
				)
			: documentsResponse;
	};

	const attachedDocuments =
		documentType === 'formative'
			? proposal?.formativeDocuments
			: getSectionDocuments();

	const handleSubmit = async () => {
		const query = queryMessage || proposal?.rfpSummary;
		if (!query) return;
		setIsMessageLoading(true);
		const response = await findDocuments(query);
		if (response.statusCode === 201) {
			const documentsResponse = (
				response.data as { documents: FindDocuments[] }
			).documents;
			const filteredDocuments = getFilteredDocuments(documentsResponse);
			setDocuments(filteredDocuments);
			setIsSuggestedList(queryMessage === '');
			setIsMessageLoading(false);
		} else {
			console.log('error');
			setIsMessageLoading(false);
		}
	};

	const debouncedSearchChange = useDebounce(handleSubmit, 600);

	useEffect(() => {
		if (proposal?.rfpSummary) {
			handleSubmit();
		}
	}, []);

	return (
		<div className="flex flex-col h-full p-4 overflow-hidden bg-white rounded-xl shadow-main">
			<div className="flex items-center justify-between pb-3 xl:pb-4">
				<div className="relative flex w-full">
					<Input
						value={queryMessage}
						onChange={(event) => {
							setQueryMessage(event.target.value);
							debouncedSearchChange();
						}}
						placeholder="Search"
						className="bg-white border-[#D3DDE2] h-12 py-2 pr-10 rounded-xl hover:bg-[#EAF1FC] disabled:bg-[#F5F5F5]"
					/>
					<div className="absolute right-3 top-3">
						{queryMessage !== '' ? (
							<X
								onClick={() => {
									setQueryMessage('');
									debouncedSearchChange();
								}}
								className="pt-1 cursor-pointer"
								color="#5D6F79"
								size={20}
							/>
						) : (
							<Search color="#5D9BFD" className="pt-1 w-5 h-5" />
						)}
					</div>
				</div>
			</div>
			<MessagesList
				setDocLoading={setDocLoading}
				setDocData={setDocData}
				list={documents as FindDocuments[]}
				isMessageLoading={isMessageLoading}
				isSuggestedList={isSuggestedList}
			/>
		</div>
	);
};

export default DocumentChatBox;
